import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Users, AlertCircle, Scale, Globe, Mail, Phone, MapPin } from 'lucide-react';
import { ROUTES } from '@constants';
import Button from '@components/common/Button';
import Card from '@components/common/Card';

const TermsAndConditions = () => {
  const sections = [
    {
      id: 'agreement',
      title: 'Agreement to Our Legal Terms',
      icon: FileText,
      content: (
        <>
          <p className="mb-4">
            We are <strong>Flatout way Consultancy</strong>, doing business as Flatout way Consultancy ('Company', 'we', 'us', or 'our'), 
            a company registered in India at 1st Floor -B-23 618/1, Ravidass Nagar, Hoshiarpur, PUNJAB 146001, Hoshiarpur, Punjab 146001.
          </p>
          <p className="mb-4">
            We operate the website <strong>flatoutway.com</strong> (the 'Site'), as well as any other related products and services that 
            refer or link to these legal terms (the 'Legal Terms') (collectively, the 'Services').
          </p>
          <p className="mb-4">
            These Legal Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity ('you'), 
            and Flatout way Consultancy, concerning your access to and use of the Services. You agree that by accessing the Services, you have 
            read, understood, and agreed to be bound by all of these Legal Terms.
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
            <p className="text-sm text-yellow-800 font-semibold">
              IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST 
              DISCONTINUE USE IMMEDIATELY.
            </p>
          </div>
        </>
      ),
    },
    {
      id: 'services',
      title: 'Our Services',
      icon: Globe,
      content: (
        <p>
          The information provided when using the Services is not intended for distribution to or use by any person or entity in any 
          jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any 
          registration requirement within such jurisdiction or country.
        </p>
      ),
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property Rights',
      icon: Shield,
      content: (
        <>
          <h4 className="font-semibold text-primary-900 mb-2">Our intellectual property</h4>
          <p className="mb-4">
            We are the owner or the licensee of all intellectual property rights in our Services, including all source code, databases, 
            functionality, software, website designs, audio, video, text, photographs, and graphics in the Services (collectively, the 'Content'), 
            as well as the trademarks, service marks, and logos contained therein (the 'Marks').
          </p>
          <p className="mb-4">
            Our Content and Marks are protected by copyright and trademark laws and treaties in the United States and around the world.
          </p>
          <h4 className="font-semibold text-primary-900 mb-2">Your use of our Services</h4>
          <p className="mb-4">
            Subject to your compliance with these Legal Terms, we grant you a non-exclusive, non-transferable, revocable licence to:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
            <li>Access the Services; and</li>
            <li>Download or print a copy of any portion of the Content to which you have properly gained access</li>
          </ul>
          <p>
            Solely for your personal, non-commercial use or internal business purpose.
          </p>
        </>
      ),
    },
    {
      id: 'user-representations',
      title: 'User Representations',
      icon: Users,
      content: (
        <>
          <p className="mb-4">By using the Services, you represent and warrant that:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>All registration information you submit will be true, accurate, current, and complete</li>
            <li>You will maintain the accuracy of such information and promptly update such registration information as necessary</li>
            <li>You have the legal capacity and you agree to comply with these Legal Terms</li>
            <li>You are not a minor in the jurisdiction in which you reside, or if a minor, you have received parental permission to use the Services</li>
            <li>You will not access the Services through automated or non-human means, whether through a bot, script or otherwise</li>
            <li>You will not use the Services for any illegal or unauthorised purpose</li>
            <li>Your use of the Services will not violate any applicable law or regulation</li>
          </ul>
        </>
      ),
    },
    {
      id: 'user-registration',
      title: 'User Registration',
      icon: FileText,
      content: (
        <p>
          You may be required to register to use the Services. You agree to keep your password confidential and will be responsible for all 
          use of your account and password. We reserve the right to remove, reclaim, or change a username you select if we determine, in our 
          sole discretion, that such username is inappropriate, obscene, or otherwise objectionable.
        </p>
      ),
    },
    {
      id: 'purchases-payment',
      title: 'Purchases and Payment',
      icon: FileText,
      content: (
        <>
          <p className="mb-4">We accept the following forms of payment: Discover</p>
          <p className="mb-4">
            You agree to provide current, complete, and accurate purchase and account information for all purchases made via the Services. 
            Sales tax will be added to the price of purchases as deemed required by us. We may change prices at any time. All payments shall 
            be in Rupees.
          </p>
          <p>
            We reserve the right to refuse any order placed through the Services. We may, in our sole discretion, limit or cancel quantities 
            purchased per person, per household, or per order.
          </p>
        </>
      ),
    },
    {
      id: 'prohibited-activities',
      title: 'Prohibited Activities',
      icon: AlertCircle,
      content: (
        <>
          <p className="mb-4">
            You may not access or use the Services for any purpose other than that for which we make the Services available. The Services may 
            not be used in connection with any commercial endeavours except those that are specifically endorsed or approved by us.
          </p>
          <p className="mb-4">As a user of the Services, you agree not to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
            <li>Systematically retrieve data or other content from the Services to create or compile a collection, compilation, database, or directory</li>
            <li>Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information</li>
            <li>Circumvent, disable, or otherwise interfere with security-related features of the Services</li>
            <li>Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Services</li>
            <li>Use any information obtained from the Services in order to harass, abuse, or harm another person</li>
            <li>Use the Services in a manner inconsistent with any applicable laws or regulations</li>
            <li>Engage in unauthorised framing of or linking to the Services</li>
            <li>Upload or transmit viruses, Trojan horses, or other material that interferes with any party's uninterrupted use of the Services</li>
            <li>Engage in any automated use of the system, such as using scripts to send comments or messages</li>
            <li>Interfere with, disrupt, or create an undue burden on the Services or the networks or services connected to the Services</li>
          </ul>
        </>
      ),
    },
    {
      id: 'privacy-policy',
      title: 'Privacy Policy',
      icon: Shield,
      content: (
        <p>
          We care about data privacy and security. By using the Services, you agree to be bound by our Privacy Policy posted on the Services, 
          which is incorporated into these Legal Terms. Please be advised the Services are hosted in India. If you access the Services from any 
          other region of the world with laws or other requirements governing personal data collection, use, or disclosure that differ from 
          applicable laws in India, then through your continued use of the Services, you are transferring your data to India, and you expressly 
          consent to have your data transferred to and processed in India.
        </p>
      ),
    },
    {
      id: 'term-termination',
      title: 'Term and Termination',
      icon: AlertCircle,
      content: (
        <>
          <p className="mb-4">
            These Legal Terms shall remain in full force and effect while you use the Services. WITHOUT LIMITING ANY OTHER PROVISION OF THESE 
            LEGAL TERMS, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE SERVICES 
            (INCLUDING BLOCKING CERTAIN IP ADDRESSES), TO ANY PERSON FOR ANY REASON OR FOR NO REASON.
          </p>
          <p>
            We may terminate your use or participation in the Services or delete your account and any content or information that you posted at 
            any time, without warning, in our sole discretion.
          </p>
        </>
      ),
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      icon: Scale,
      content: (
        <p>
          These Legal Terms shall be governed by and defined following the laws of India. Flatout way Consultancy and yourself irrevocably 
          consent that the courts of India shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these 
          Legal Terms.
        </p>
      ),
    },
    {
      id: 'dispute-resolution',
      title: 'Dispute Resolution',
      icon: Scale,
      content: (
        <>
          <h4 className="font-semibold text-primary-900 mb-2">Informal Negotiations</h4>
          <p className="mb-4">
            To expedite resolution and control the cost of any dispute, controversy, or claim related to these Legal Terms, the Parties agree 
            to first attempt to negotiate any Dispute informally for at least fifteen (15) days before initiating arbitration.
          </p>
          <h4 className="font-semibold text-primary-900 mb-2">Binding Arbitration</h4>
          <p className="mb-4">
            Any dispute arising out of or in connection with these Legal Terms shall be referred to and finally resolved by the International 
            Commercial Arbitration Court under the European Arbitration Chamber according to its Rules. The seat of arbitration shall be Punjab, 
            India. The language of the proceedings shall be English.
          </p>
        </>
      ),
    },
    {
      id: 'disclaimer',
      title: 'Disclaimer',
      icon: AlertCircle,
      content: (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
          <p className="text-sm text-red-800 font-semibold uppercase">
            THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE SERVICES WILL BE AT YOUR SOLE RISK. 
            TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SERVICES AND YOUR 
            USE THEREOF.
          </p>
        </div>
      ),
    },
    {
      id: 'limitations-liability',
      title: 'Limitations of Liability',
      icon: Shield,
      content: (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
          <p className="text-sm text-red-800 font-semibold uppercase">
            IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, 
            CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER 
            DAMAGES ARISING FROM YOUR USE OF THE SERVICES.
          </p>
        </div>
      ),
    },
    {
      id: 'contact',
      title: 'Contact Us',
      icon: Mail,
      content: (
        <>
          <p className="mb-4">
            In order to resolve a complaint regarding the Services or to receive further information regarding use of the Services, please 
            contact us at:
          </p>
          <div className="bg-primary-50 rounded-lg p-6 space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-primary-900">Flatout way Consultancy</p>
                <p className="text-sm text-primary-700">1st Floor -B-23 618/1, Ravidass Nagar, Hoshiarpur</p>
                <p className="text-sm text-primary-700">PUNJAB 146001, India</p>
              </div>
            </div>
            {/* <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <a href="tel:09888892022" className="text-sm text-primary-700 hover:text-primary-900 transition-colors">
                09888892022
              </a>
            </div> */}
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <a href="mailto:flatoutway@gmail.com" className="text-sm text-primary-700 hover:text-primary-900 transition-colors">
                flatoutway@gmail.com
              </a>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-100 py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-secondary-100 rounded-full">
              <span className="text-sm font-semibold text-secondary-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Legal Information
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-900 mb-4">
              Terms and Conditions
            </h1>
            
            <p className="text-base sm:text-lg text-primary-600 mb-6">
              Last updated: March 26, 2026
            </p>
            
            <p className="text-base text-primary-700 max-w-2xl mx-auto">
              Please read these terms and conditions carefully before using our services. By accessing or using F.O.W, 
              you agree to be bound by these terms.
            </p>
            
            <div className="mt-8">
              <Link to={ROUTES.HOME}>
                <Button variant="outline" size="md" className="inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-primary-900 mb-4">Table of Contents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-primary-600 hover:text-primary-900 hover:underline transition-colors"
                >
                  {index + 1}. {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <Card key={section.id} id={section.id} className="p-6 md:p-8 scroll-mt-24">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-secondary-600 mb-1">
                        Section {index + 1}
                      </div>
                      <h2 className="text-2xl font-bold text-primary-900">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                  <div className="text-primary-700 leading-relaxed">
                    {section.content}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-primary-900 mb-4">
              Questions About Our Terms?
            </h3>
            <p className="text-base text-primary-600 mb-6">
              If you have any questions or concerns about these terms and conditions, please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="mailto:flatoutway@gmail.com">
                <Button variant="primary" size="lg" className="inline-flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact Us
                </Button>
              </a>
              <Link to={ROUTES.HOME}>
                <Button variant="outline" size="lg" className="inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;
